import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ENVIRONMENT } from '../../../../environment/environment';
import { DeviceModel } from '../../../features/authentication/models/authentication.model';
import { StorageService } from '../storage.service';

/**
 * All tracking event must add at this enum
 * Event Enum values must match with backend, deploy backend before frontend
 */
export enum TrackingEventType {
    AskpodIconClick = 'AskpodIconClick',
}

/**
 * General function for the backend event-tracking endpoint.
 */
@Injectable({
    providedIn: 'root',
})
export class TrackingService {
    private readonly endpoint = `${ENVIRONMENT.apiDomain}/api/v1/tracking/events`;

    constructor(
        private storageService: StorageService,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {}

    /**
     * Records a single user-interaction event. Fire-and-forget.
     * Using fetch() because fetch() tells browser to keep the request alive even when the document is unloaded or being navigated. HttpClient cannot do that.
     *
     * @param eventType Type of event (must match the backend enum).
     * @param url The page URL where it occurred. Defaults to the current location.
     * @param payload Optional extra fields (size limit is capped at backend).
     */
    public trackEvent(eventType: TrackingEventType, url?: string, payload?: unknown): void {
        // Tracking relies on browser-only APIs (fetch/document/navigator); never run during SSR.
        if (!isPlatformBrowser(this.platformId)) return;

        try {
            const token = this.storageService.accessToken;
            if (!token) return;

            const resolvedUrl = url ?? this.document.location.href;

            // Retrieve device type on client's end.
            const deviceType = DeviceModel.detectDeviceType();

            void fetch(this.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ eventType, url: resolvedUrl, deviceType, payload }),
                keepalive: true,
            }).catch((err) => console.warn('[tracking] failed to send event', eventType, err));
        } catch (err) {
            // No action here as a silent track analytics fail is acceptable, and we do not want to prompt client for any tracking error.
            // *Note: Backend already handle the request with Hangfire, and it comes with 3x retry mechanism.
            console.warn('[tracking] unexpected error sending event', eventType, err);
        }
    }
}
